import type { Theme } from "./theme";

/**
 * Anivaze v4 launch themes. Studio + Bold are the Phase-1 pair (PLAN §18);
 * Editorial + Night are token-only additions so the live picker shows the
 * re-theme power. The childish v3 skins (Comic-Sans/pastel) are retired as the
 * identity; Rough.js returns later as a "Paper" theme (roughness > 0).
 */

const SANS = "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const CONDENSED = "'Bahnschrift', 'DIN Alternate', 'Arial Narrow', Inter, sans-serif";
const SERIF = "Georgia, 'Iowan Old Style', 'Times New Roman', serif";

/** Clean modern vector — the default. Confident, neutral, product-grade. */
export const studioTheme: Theme = {
  id: "studio",
  label: "Studio",
  dark: false,
  palette: {
    ink: "#0f1620", sub: "#3d4a5c", surface: "#eef3fb", surface2: "#ffffff",
    primary: "#2f6df0", secondary: "#7c8aa0", positive: "#12a877",
    danger: "#e5484d", highlight: "#2f6df0", muted: "#9aa7bd",
  },
  surface: { kind: "linear", colors: ["#f3f7fe", "#e2eaf6"], angle: 155, grain: 0, vignette: 0.05 },
  display: { family: SANS, weight: 800, tracking: -0.02, upper: false },
  title: { family: SANS, weight: 700, tracking: -0.01, upper: false },
  body: { family: SANS, weight: 500, tracking: 0, upper: false },
  caption: {
    family: SANS, weight: 800, upper: false, tracking: -0.01,
    fill: "#0f1620", highlight: "#2f6df0", box: "outline", boxColor: "#ffffff", sizeUnit: 4.2,
  },
  radius: 22, strokeWidth: 3, roughness: 0, motion: "smooth",
};

/** High-contrast, condensed display, punchy accent, snappy overshoot — the
 *  social short-form workhorse. */
export const boldTheme: Theme = {
  id: "bold",
  label: "Bold",
  dark: true,
  palette: {
    ink: "#f6f4ef", sub: "#c3c9d1", surface: "#14181d", surface2: "#1d2329",
    primary: "#ff5d33", secondary: "#ffb020", positive: "#37d39a",
    danger: "#ff5d33", highlight: "#ff6a42", muted: "#6d7681",
  },
  surface: { kind: "linear", colors: ["#20262d", "#12151a"], angle: 155, grain: 0.05, vignette: 0.28 },
  display: { family: CONDENSED, weight: 700, tracking: -0.005, upper: true },
  title: { family: CONDENSED, weight: 700, tracking: 0, upper: true },
  body: { family: SANS, weight: 600, tracking: 0, upper: false },
  caption: {
    family: CONDENSED, weight: 700, upper: true, tracking: 0,
    fill: "#f6f4ef", highlight: "#ff6a42", box: "outline", boxColor: "#12151a", sizeUnit: 4.8,
  },
  radius: 14, strokeWidth: 4, roughness: 0, motion: "snappy",
};

/** Calm serif, warm ground, slow smooth motion — documentary / story. */
export const editorialTheme: Theme = {
  id: "editorial",
  label: "Editorial",
  dark: false,
  palette: {
    ink: "#221f1a", sub: "#5b544a", surface: "#f7f4ee", surface2: "#fffdf8",
    primary: "#c0662f", secondary: "#8a7d67", positive: "#5a7d4e",
    danger: "#b23a2e", highlight: "#c0662f", muted: "#b7ad9c",
  },
  surface: { kind: "linear", colors: ["#f8f5ef", "#e9e2d5"], angle: 160, grain: 0.03, vignette: 0.08 },
  display: { family: SERIF, weight: 700, tracking: 0, upper: false },
  title: { family: SERIF, weight: 700, tracking: 0, upper: false },
  body: { family: SERIF, weight: 400, tracking: 0, upper: false },
  caption: {
    family: SERIF, weight: 600, upper: false, tracking: 0,
    fill: "#221f1a", highlight: "#c0662f", box: "none", boxColor: "#fffdf8", sizeUnit: 4.4,
  },
  radius: 8, strokeWidth: 2.5, roughness: 0, motion: "smooth",
};

/** Dark moody glow — motivational / quote. */
export const nightTheme: Theme = {
  id: "night",
  label: "Night",
  dark: true,
  palette: {
    ink: "#eef0ff", sub: "#b7bce0", surface: "#0c0b1a", surface2: "#191634",
    primary: "#8b7bff", secondary: "#f06bd0", positive: "#4fd6c0",
    danger: "#ff6b8b", highlight: "#a695ff", muted: "#5a577f",
  },
  surface: { kind: "radial", colors: ["#2a2350", "#0c0b1a"], angle: 0, grain: 0.06, vignette: 0.35 },
  display: { family: SANS, weight: 800, tracking: -0.015, upper: true },
  title: { family: SANS, weight: 700, tracking: -0.01, upper: false },
  body: { family: SANS, weight: 500, tracking: 0, upper: false },
  caption: {
    family: SANS, weight: 800, upper: false, tracking: -0.01,
    fill: "#eef0ff", highlight: "#a695ff", box: "outline", boxColor: "#0c0b1a", sizeUnit: 4.4,
  },
  radius: 20, strokeWidth: 3, roughness: 0, motion: "kinetic",
};

/** Chalkboard — the Teacher pack look: Khan-style board, chalk inks, live
 *  handwriting (beats render via BoardScene when this theme is active). */
export const chalkTheme: Theme = {
  id: "chalk",
  label: "Chalkboard",
  dark: true,
  palette: {
    ink: "#eee9dc", sub: "#c9c3b4", surface: "#16211b", surface2: "#1d2b23",
    primary: "#f5d76e", secondary: "#8fd0ff", positive: "#9fe6a0",
    danger: "#ff9f80", highlight: "#8fd0ff", muted: "#6d7a71",
  },
  surface: { kind: "radial", colors: ["#1d2b23", "#101a14"], angle: 0, grain: 0.08, vignette: 0.32 },
  display: { family: "'Segoe Print','Comic Sans MS','Chalkboard SE',cursive", weight: 700, tracking: 0.01, upper: false },
  title: { family: "'Segoe Print','Comic Sans MS','Chalkboard SE',cursive", weight: 700, tracking: 0.01, upper: false },
  body: { family: "'Segoe Print','Comic Sans MS','Chalkboard SE',cursive", weight: 600, tracking: 0.01, upper: false },
  caption: {
    family: "'Segoe Print','Comic Sans MS','Chalkboard SE',cursive", weight: 700, upper: false, tracking: 0.01,
    fill: "#eee9dc", highlight: "#f5d76e", box: "outline", boxColor: "#101a14", sizeUnit: 4.0,
  },
  radius: 10, strokeWidth: 2.6, roughness: 1.2, motion: "smooth",
};

const HAND = "'Segoe Print','Bradley Hand','Comic Sans MS',cursive";

/** Whiteboard Explainer (the Writer/whiteboard YouTube pack): warm paper, ink,
 *  and the hand-drawn SKETCH filter (theme.roughness>0 → Stage applies the
 *  turbulence wobble) so every actor reads as drawn by hand. */
export const paperTheme: Theme = {
  id: "paper",
  label: "Hand-drawn",
  dark: false,
  palette: {
    ink: "#2b2a26", sub: "#6f6c63", surface: "#f6f1e6", surface2: "#fffdf6",
    primary: "#2f6db0", secondary: "#d1863f", positive: "#4f8a4f",
    danger: "#c24a3a", highlight: "#e0a53b", muted: "#cfc9ba",
  },
  surface: { kind: "flat", colors: ["#f6f1e6"], angle: 0, grain: 0.06, vignette: 0.04 },
  display: { family: HAND, weight: 700, tracking: 0, upper: false },
  title: { family: HAND, weight: 700, tracking: 0, upper: false },
  body: { family: HAND, weight: 600, tracking: 0, upper: false },
  caption: {
    family: HAND, weight: 700, upper: false, tracking: 0,
    fill: "#2b2a26", highlight: "#2f6db0", box: "outline", boxColor: "#fffdf6", sizeUnit: 4.3,
  },
  radius: 16, strokeWidth: 3, roughness: 1.1, motion: "smooth",
};

/** Flat Motion (the Charles Schwab premium motion-graphics pack): muted teal /
 *  gold, soft, clean — high perceived polish, pure vector. */
export const flatTheme: Theme = {
  id: "flat",
  label: "Flat Motion",
  dark: false,
  palette: {
    ink: "#1f3b3a", sub: "#4d6b69", surface: "#eaf3f1", surface2: "#ffffff",
    primary: "#2fa39a", secondary: "#e0a24e", positive: "#3fae7a",
    danger: "#e0685a", highlight: "#2fa39a", muted: "#a9c3bf",
  },
  surface: { kind: "linear", colors: ["#eef6f4", "#dbeae6"], angle: 150, grain: 0, vignette: 0.05 },
  display: { family: SANS, weight: 800, tracking: -0.015, upper: false },
  title: { family: SANS, weight: 700, tracking: -0.01, upper: false },
  body: { family: SANS, weight: 500, tracking: 0, upper: false },
  caption: {
    family: SANS, weight: 800, upper: false, tracking: -0.01,
    fill: "#1f3b3a", highlight: "#2fa39a", box: "pill", boxColor: "#ffffff", sizeUnit: 4.2,
  },
  radius: 24, strokeWidth: 3, roughness: 0, motion: "smooth",
};

/** Whiteboard DOODLE (the InstaDoodle/Doodly/VideoScribe category): crisp WHITE
 *  board, thin black ink, ONE marker accent — and (via beats.tsx) a drawing HAND
 *  that progressively sketches each beat's visual onto the board. */
export const doodleTheme: Theme = {
  id: "doodle",
  label: "Whiteboard doodle",
  dark: false,
  palette: {
    ink: "#232629", sub: "#5c6167", surface: "#fdfdfb", surface2: "#ffffff",
    primary: "#2464c7", secondary: "#d8432f", positive: "#3d8f4f",
    danger: "#d8432f", highlight: "#d8432f", muted: "#c3c7cb",
  },
  surface: { kind: "flat", colors: ["#fdfdfb"], angle: 0, grain: 0.03, vignette: 0.06 },
  display: { family: HAND, weight: 700, tracking: 0, upper: false },
  title: { family: HAND, weight: 700, tracking: 0, upper: false },
  body: { family: HAND, weight: 600, tracking: 0, upper: false },
  caption: {
    family: HAND, weight: 700, upper: false, tracking: 0,
    fill: "#232629", highlight: "#2464c7", box: "outline", boxColor: "#ffffff", sizeUnit: 4.3,
  },
  radius: 10, strokeWidth: 2.6, roughness: 0.9, motion: "smooth",
};

export const THEMES: Theme[] = [studioTheme, boldTheme, editorialTheme, nightTheme, flatTheme, paperTheme, doodleTheme, chalkTheme];
const BY_ID: Record<string, Theme> = Object.fromEntries(THEMES.map((t) => [t.id, t]));

export const getTheme = (id: string | undefined | null): Theme => (id && BY_ID[id]) || studioTheme;
